/**
 * A resource-valued property as its property bag with the pools its own references resolve
 * against. A `SubResource("id")` resolves synchronously against `pools`. An `ExtResource("id")`
 * loads its file through the resource pipeline and resolves against the file's own pools, whose
 * ids are scoped to that file.
 */
import { useMemo } from 'react';
import { resolveSubResourceRef, parsedResourcePath } from './SubResourceResolver';
import { useResource } from './useResource';
import { scopedSection } from './resourceSection';
import { resourceFilePath } from './subResourcePath';
import { holdsResource } from '../godot/scene/resourceLiteral';
const NO_RESOURCE = {
    scoped: null,
    status: 'unavailable',
    resourcePath: null,
    isNone: true,
};
const UNRESOLVED = {
    scoped: null,
    status: 'unavailable',
    resourcePath: null,
    isNone: false,
};
/**
 * @canonical A resource property that names a SubResource or an ExtResource
 */
export function useResourceResolution(ref, pools) {
    const inline = resolveSubResourceRef(ref, pools.internalResources);
    const fileResolution = usePathResolution(inline ? null : parsedResourcePath(ref, pools.externalResources));
    const inlineResolution = useMemo(() => inline
        ? {
            scoped: { resource: inline, resources: pools },
            status: 'loaded',
            resourcePath: null,
            isNone: false,
        }
        : null, [inline, pools]);
    if (inlineResolution)
        return inlineResolution;
    return holdsResource(ref) ? fileResolution : NO_RESOURCE;
}
/**
 * The resource `path` addresses, a file's `[resource]` body or a **Sub-resource path**'s
 * `[sub_resource]`, resolved against that file's own tables. Unavailable for no path, and for an id
 * the file does not declare.
 */
export function usePathResolution(path) {
    // `''` requests nothing.
    const file = useResource(path ? resourceFilePath(path) : '', 'resource');
    // Apart from any scene tables: a scene edit leaves the file's resource the same object while the file holds.
    return useMemo(() => {
        if (!path)
            return UNRESOLVED;
        if (!file.value) {
            return {
                scoped: null,
                status: file.status === 'pending' ? 'pending' : 'unavailable',
                resourcePath: path,
                isNone: false,
            };
        }
        const scoped = scopedSection(file.value, path);
        if (!scoped)
            return { scoped: null, status: 'unavailable', resourcePath: path, isNone: false };
        return { scoped, status: 'loaded', resourcePath: path, isNone: false };
    }, [path, file.status, file.value]);
}
/** The resource `ref` names in `pools`, or null while it is none, still loading or unreadable. */
export function useSubOrExtResource(ref, pools) {
    return useResourceResolution(ref, pools).scoped;
}
//# sourceMappingURL=useSubOrExtResource.js.map